import { Check, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  THEME_COLORS,
  BANNER_PATTERNS,
  BANNER_PATTERN_IDS,
  THEME_COLOR_VALUES,
} from "../../../shared/profilePresets";
import { cn } from "@/lib/utils";

interface PersonalisePanelProps {
  themeColor: string;
  bannerPatternId?: string | null;
  bannerAccentMode: string;
  onUpdateThemeColor: (color: string) => void;
  onUpdateBannerPattern: (patternId: string) => void;
  onResetToAuto: () => void;
}

export function PersonalisePanel({
  themeColor,
  bannerPatternId,
  bannerAccentMode,
  onUpdateThemeColor,
  onUpdateBannerPattern,
  onResetToAuto,
}: PersonalisePanelProps) {
  return (
    <Card data-testid="personalise-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Personalise</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs text-muted-foreground mb-2">Theme Color</p>
          <div className="flex gap-2" data-testid="theme-color-swatches">
            {THEME_COLOR_VALUES.map((key) => {
              const color = THEME_COLORS[key];
              return (
                <button
                  key={key}
                  data-testid={`theme-color-${key}`}
                  onClick={() => onUpdateThemeColor(key)}
                  className={cn(
                    "w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors",
                    color.bgClass,
                    themeColor === key
                      ? "border-foreground ring-2 ring-foreground/20"
                      : "border-transparent hover:border-foreground/40"
                  )}
                  title={color.label}
                >
                  {themeColor === key && (
                    <Check className="h-4 w-4 text-white drop-shadow-md" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-2">Banner Pattern</p>
          <div
            className="flex flex-wrap gap-2"
            data-testid="banner-pattern-options"
          >
            {BANNER_PATTERN_IDS.map((id) => {
              const p = BANNER_PATTERNS[id];
              return (
                <button
                  key={id}
                  data-testid={`banner-pattern-${id}`}
                  onClick={() => onUpdateBannerPattern(id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all",
                    bannerPatternId === id
                      ? "border-primary bg-primary/10 font-medium"
                      : "border-border hover:border-primary/50 hover:bg-muted"
                  )}
                  title={p.description}
                >
                  <span>{p.emoji}</span>
                  <span className="text-xs">{p.label}</span>
                </button>
              );
            })}
          </div>
          {bannerAccentMode === "custom" && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 text-xs"
              onClick={onResetToAuto}
              data-testid="reset-banner-auto"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset to auto
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
